import React, {
  useContext, useMemo, useState, useEffect,
} from "react";
import Form from "components/Form/Form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import LoadingCurtain from "components/LoadingCurtain";
import useAuth from "widgets/Onboarding/hooks/useAuth";
import { createYupSchema } from "components/Form/utilities/yupSchemaGenerator";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import Bodycopy from "../Bodycopy/Bodycopy";
import OnboardingSellingPoints from "../SellingPoints";
import { StepContext } from "../../onboarding";

let validationFields = [];
const userFields = ["email", "firstName", "lastName"];

const Step = ({
  step, form, bodyCopy, onSubmit,
}) => {
  const stepConfig = step;
  const path = step.path;
  const formConfig = useMemo(() => form ?? [], [form]);
  const [validationSchema, updateValidationSchema] = useState();
  const loadingText = "Loading";
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const steps = useContext(StepContext);
  const { translate: t } = useLanguageProvider();
  const [storedFields, setStoredFields] = useState({});

  useEffect(() => {
    const storedFieldsJSON = JSON.parse(localStorage.getItem("onBoarding"));
    if (storedFieldsJSON) {
      const fields = Object.fromEntries(storedFieldsJSON.map((field) => field));
      setStoredFields(fields);
    }
  }, []);

  useMemo(() => {
    validationFields = [];
    formConfig.forEach((inputRow) => {
      validationFields.push(...inputRow.fields);
    });
    validationFields = [].concat(...validationFields);

    const filteredFields = validationFields.filter((field) => {
      if (user) {
        return !userFields.includes(field.name);
      }
      return true;
    });

    const reducedFields = filteredFields.reduce(createYupSchema, {});
    updateValidationSchema(reducedFields);
    setIsLoading(false);
  }, [formConfig, user]);

  const validationResolver = yup.object().shape(validationSchema);

  const stepNames = useMemo(
    () => steps.map((step) => _.toLower(_.replace(step.path, "/", ""))),
    [steps],
  );

  const filteredConfig = useMemo(
    () =>
      formConfig.map((row) => ({
        title: row.title,
        classes: row.classes,
        fields: row.fields.filter((field) => {
          if (userFields.includes(field.name)) {
            field.defaultValue = user ? user[field.name] : "";
            field.disabled = !!user;
          }
          return true;
        }),
      })),
    [formConfig, user],
  );

  const formDefaults = useMemo(
    () => (formConfig) => {
      const defaults = {};

      formConfig.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.name && field.defaultValue !== undefined) {
            defaults[field.name] = field.defaultValue;
          }
        });
      });

      return defaults;
    },
    [],
  );

  const methods = useForm({
    resolver: yupResolver(validationResolver),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { ...formDefaults(formConfig), ...storedFields },
  });

  useEffect(() => {
    if (storedFields && Object.keys(storedFields).length !== 0) {
      methods.reset(storedFields);
    }
  }, [storedFields, methods]);

  const {
    formState: { isValid, errors },
  } = methods;
  const [queryParameters] = useSearchParams();

  const updateQueryData = (data) => {
    const fieldData = { ...data };

    queryParameters.forEach((value, query) => {
      if (_.indexOf(stepNames, query) >= 0) {
        if (fieldData[`${query}`] === undefined) {
          fieldData[`${query}`] = value;
        }
      }
    });

    return fieldData;
  };

  const handleSubmit = async (data) => {
    const formData = await updateQueryData(data, steps);
    onSubmit(formData, isValid, errors, path);
  };

  const loginScreen = stepConfig.path === "/step-2" || stepConfig.path === "/you";

  return (
    <LoadingCurtain isLoading={isLoading} loadingText={loadingText}>
      <div className="step">
        <Title user={user} stepConfig={stepConfig} />
        {!!loginScreen && user && <LogOutButton logout={logout} />}
        {stepConfig.subtitle && <h4>{t(stepConfig.subtitle)}</h4>}
        <FormProvider {...methods}>
          {bodyCopy && <Bodycopy bodyCopy={bodyCopy} />}
          <Form handleSubmit={handleSubmit} formContent={filteredConfig} key={stepConfig.name} />
        </FormProvider>
      </div>
      <OnboardingSellingPoints />
      <small className="terms">
        <a href="/terms-conditions" target="_blank" rel="noreferrer">
          {t("By starting a campaign, you agree with Chuffed.org's terms and conditions")}
        </a>
      </small>
    </LoadingCurtain>
  );
};

const LogOutButton = ({ logout }) => {
  const { translate: t } = useLanguageProvider();
  return (
    <button type="button" onClick={logout} className="link-button --small --secondary-outline mb-2">
      { t("Not you? Logout") }
    </button>
  );
};

const Title = ({ user, stepConfig }) => {
  const { translate: t } = useLanguageProvider();
  if (user && stepConfig.altTitle) {
    return (
      <h2 style={{ marginBottom: "1.6rem" }}>
        {t(stepConfig.altTitle)}
        {" "}
        {user.firstName}
        !
      </h2>
    );
  }
  return <h2 style={{ marginBottom: "1.6rem" }}>{ t(stepConfig.title) }</h2>;
};

export default Step;
